"use client";

import { useAuth } from "@/hooks/use-auth";
import { deepEqual as isEqualFunc } from "fast-equals";
import { Plus, Trash2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Label } from "react-aria-components";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { propertyTypes } from "../../info/unit/property-types";
import { PlaceAutocomplete } from "../others/place-autocomplete";
import type { PlaceDetails } from "../others/place-autocomplete/types";
import { CheckboxGroupField } from "../rhf/checkbox-group-field";
import { Input } from "../rhf/input";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
import { getStepDescription } from "./steps/config";
interface ProjectData {
  id: string;
  name: string;
  totalFlats: number;
  noOfFlatsForSale: number;
  propertyTypes: string[];
}
interface BuilderOrLandownerOrInvestorProjectFormData {
  projects: ProjectData[];
}
interface BuilderLandoOrwnerInvesOrtorPropsProject {
  onNext: (data: BuilderOrLandownerOrInvestorProjectFormData) => void;
  onBack: () => void;
  isLoading?: boolean;
}
export function BuilderOrLandownerOrInvestorProject({
  onNext,
  onBack,
  isLoading
}: BuilderLandoOrwnerInvesOrtorPropsProject) {
  const {
    user
  } = useAuth();
  const searchParams = useSearchParams();
  const groupId = searchParams?.get("groupId") || null;
  const {
    state,
    updateStepData
  } = useFormContext();

  // Get relationship from profile state to customize labels
  const relationship = state.profile?.relationship;
  const groupType = ["owner", "agent"].includes(relationship || "") ? "individual" : relationship || "individual";
  const groupProjects = user?.groups?.find(g => g.id === groupId || g.relationship === "seller" && g.type === groupType)?.projects?.map(p => ({
    id: p.project.id,
    name: p.project.name,
    totalFlats: p.totalFlats,
    noOfFlatsForSale: p.noOfFlatsForSale,
    propertyTypes: p.propertyTypes || []
  }));
  // Try both old and new step keys for compatibility
  const contextBuilderOrLandownerOrInvestorProject = state.builderOrLandownerOrInvestorProject as Partial<BuilderOrLandownerOrInvestorProjectFormData>;
  const defaultValues: BuilderOrLandownerOrInvestorProjectFormData = useMemo(() => ({
    projects: contextBuilderOrLandownerOrInvestorProject?.projects?.length ? contextBuilderOrLandownerOrInvestorProject.projects.map(project => ({
      ...project,
      propertyTypes: project.propertyTypes || []
    })) : groupProjects?.length ? groupProjects.map(project_0 => ({
      ...project_0,
      propertyTypes: project_0.propertyTypes || []
    })) : [{
      id: "",
      name: "",
      totalFlats: 0,
      noOfFlatsForSale: 0,
      propertyTypes: []
    }]
  }), [groupProjects, contextBuilderOrLandownerOrInvestorProject?.projects]);
  const form = useForm<BuilderOrLandownerOrInvestorProjectFormData>({
    defaultValues,
    mode: "onChange"
  });
  const {
    control,
    handleSubmit,
    formState,
    reset,
    getValues,
    setValue,
    ...useFormProps
  } = form;
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: "projects"
  });

  // Reset if defaults change
  const prevDefaults = useRef(defaultValues);
  useEffect(() => {
    if (!isEqualFunc(prevDefaults.current, defaultValues)) {
      reset(defaultValues);
      prevDefaults.current = defaultValues;
    }
  }, [defaultValues, reset]);

  // Watch form values
  const watchedValues = useWatch({
    control
  }) as BuilderOrLandownerOrInvestorProjectFormData;

  // Memoize the watched values to prevent unnecessary re-renders
  const memoizedWatchedValues = useMemo(() => watchedValues, [watchedValues]);

  // Update context on form change with debounced updates
  const prevWatched = useRef(memoizedWatchedValues);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Memoize the update function to prevent unnecessary re-renders
  const debouncedUpdate = useCallback(() => {
    if (isMountedRef.current) {
      updateStepData("builderOrLandownerOrInvestorProject", memoizedWatchedValues);
      prevWatched.current = memoizedWatchedValues;
    }
  }, [memoizedWatchedValues, updateStepData]);
  useEffect(() => {
    if (!isEqualFunc(prevWatched.current, memoizedWatchedValues)) {
      // Clear previous timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Debounce the update to prevent rapid changes while maintaining focus
      updateTimeoutRef.current = setTimeout(debouncedUpdate, 300);
    }
  }, [memoizedWatchedValues, debouncedUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Handle place selection for project name
  const handlePlaceSelect = useCallback((place: PlaceDetails, index: number) => {
    const currentValues = getValues();
    const updatedProjects = [...currentValues.projects];
    updatedProjects[index] = {
      ...updatedProjects[index],
      id: place.id,
      name: place.displayName || place.formattedAddress
    };
    setValue("projects", updatedProjects, {
      shouldValidate: true,
      shouldDirty: true
    });
  }, [getValues, setValue]);

  // Submit
  const onSubmit = (data: BuilderOrLandownerOrInvestorProjectFormData) => {
    onNext(data);
  };

  // Only allow next if all required values are provided for all projects
  const canProceed = watchedValues.projects && watchedValues.projects.length > 0 && watchedValues.projects.every(project_1 => project_1.name && project_1.totalFlats > 0 && project_1.noOfFlatsForSale > 0 && project_1.noOfFlatsForSale <= project_1.totalFlats && project_1.propertyTypes && project_1.propertyTypes.length > 0) && formState.isValid;

  // Dynamic content based on relationship
  const content = getStepDescription({
    stepKey: "builderOrLandownerOrInvestorProject",
    relationship,
    defaultDescription: "Tell us about your project"
  });
  return <FormProvider {...{
    control,
    handleSubmit,
    formState,
    reset,
    getValues,
    setValue,
    ...useFormProps
  }}>
			<form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
				<div className="flex-1 space-y-6 lg:space-y-8 mb-8">
					<div className="space-y-3 lg:space-y-4">
						<h2 className="text-2xl lg:text-3xl font-bold text-neutral-900 leading-tight">Project Details</h2>
						<p className="text-neutral-600 text-base lg:text-lg leading-relaxed">{content}</p>
					</div>

					<div className="space-y-4">
						{fields.map((field, index_0) => {
            const isLastItem = index_0 === fields.length - 1;
            const currentProject = watchedValues.projects?.[index_0];
            const hasContent = Boolean(currentProject?.name || currentProject?.totalFlats || currentProject?.noOfFlatsForSale || currentProject?.propertyTypes?.length);
            const allFieldsFilled = Boolean(currentProject?.name && currentProject?.totalFlats && currentProject?.noOfFlatsForSale && currentProject?.propertyTypes?.length);
            const isFirstItem = index_0 === 0;
            return <div key={field.id} className="flex gap-2 items-start">
									{/* Project inputs container */}
									<div className="flex-1 space-y-4">
										{/* Basic project info in a row */}
										<div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-2 sm:gap-4">
											{/* Search Input - Project Name */}
											<div className="relative">
												{index_0 === 0 && <Label className="text-title-medium">Project Name</Label>}
												<div className="relative">
													<PlaceAutocomplete onPlaceSelect={(place_0: PlaceDetails) => handlePlaceSelect(place_0, index_0)} placeholder="Search for project name or location..." initialValue={currentProject?.name || ""} inputProps={{
                        className: "w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
                      }} />
												</div>
											</div>

											<Input name={`projects.${index_0}.totalFlats`} label={index_0 === 0 ? "Total Units (Approx.)" : ""} type="number" placeholder="Total Units" rules={{
                    required: currentProject?.name ? "Required" : false,
                    min: {
                      value: 1,
                      message: "Must be at least 1"
                    }
                  }} control={control} />

											<Input name={`projects.${index_0}.noOfFlatsForSale`} label={index_0 === 0 ? "Units for Sale" : ""} type="number" placeholder="Flats for Sale" rules={{
                    required: currentProject?.name ? "Required" : false,
                    min: {
                      value: 1,
                      message: "Must be at least 1"
                    },
                    validate: (value: unknown) => {
                      // Only validate if project name is provided
                      if (!currentProject?.name) return true;
                      let numValue: number = 0;
                      if (typeof value === "string") {
                        numValue = parseInt(value, 10);
                      } else if (typeof value === "number") {
                        numValue = value;
                      } else {
                        return "Invalid value";
                      }
                      const totalFlats = watchedValues.projects?.[index_0]?.totalFlats || 0;
                      if (numValue > totalFlats) {
                        return "Cannot exceed total number of flats";
                      }
                      return true;
                    }
                  }} control={control} />
										</div>

										{/* Property Types Multi-Select */}
										<div className="w-full">
											<CheckboxGroupField name={`projects.${index_0}.propertyTypes`} control={control} label={index_0 === 0 ? "Property Types" : ""} options={propertyTypes.filter(propertyType => propertyType.value !== "house").map(category => ({
                    value: category.value,
                    label: category.label
                  }))} rules={{
                    required: currentProject?.name ? "Select at least one property type" : false
                  }} />
										</div>
									</div>

									{/* Action icons - reserve space to prevent layout jumping */}
									<div className={`flex gap-1 min-w-[80px] ${index_0 === 0 ? "pt-8" : "pt-1"}`}>
										{/* Delete icon - show for first row only when data available, always show for other rows */}
										{(isFirstItem ? hasContent : true) && <button type="button" onClick={() => {
                  if (isFirstItem && fields.length === 1) {
                    // Clear first item data
                    const emptyProject = {
                      id: "",
                      name: "",
                      totalFlats: 0,
                      noOfFlatsForSale: 0,
                      propertyTypes: []
                    };
                    remove(index_0);
                    append(emptyProject);
                  } else {
                    remove(index_0);
                  }
                }} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer" title={isFirstItem && fields.length === 1 ? "Clear" : "Delete"}>
												<Trash2 size={16} />
											</button>}

										{/* Plus icon - shown only on the last item when all fields are filled */}
										{isLastItem && allFieldsFilled && <button type="button" onClick={() => append({
                  id: "",
                  name: "",
                  totalFlats: 0,
                  noOfFlatsForSale: 0,
                  propertyTypes: []
                })} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" title="Add">
												<Plus size={16} />
											</button>}
									</div>
								</div>;
          })}
					</div>
				</div>
				<ButtonActions next onBack={onBack} disabled={!canProceed} isLoading={isLoading} />
			</form>
		</FormProvider>;
}